import { ReactElement, useEffect, useState } from 'react';
import moment from 'moment';
import { useData } from '../context/data.context';
import {
  Alert,
  DeviceAttributesV1,
  DeviceType,
  ParametersType,
  ParticleData
} from '../constants/telosair-interfaces';
import CustomParameterBg from './CustomParameterBg';
import { getDisplayableValue } from '../_helpers/globalHelper';
import { resolveDeviceImage } from '../services/deviceImage.service';
import { STALE_BG_CLASS, STALE_TEXT_CLASS, lastReportedLabel } from '../_helpers/staleData';

type DeviceHoverCardProps = {
  particleData: ParticleData;
  deviceData: DeviceAttributesV1;
  loading: boolean;
  alert: Alert[];
  selectedParameters: ParametersType[];
  /** The readings are the device's last known values, not from the window. */
  stale?: boolean;
  /** When that last known reading was taken. */
  asOf?: number | null;
  position: {
    x: number;
    y: number;
  };
};

/**
 * A DeviceHoverCard renders the value of its devices in the card.
 * @param props - Properties of a DeviceHoverCard component.
 * @returns - A Card on hover of a Device circle Icon.
 */
export default function DeviceHoverCard(props: DeviceHoverCardProps): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    particleData,
    deviceData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    loading,
    alert,
    selectedParameters,
    position,
    stale = false,
    asOf = null
  } = props;
  const { parameters, devices } = useData();
  // Readings arrive from the API already expressed in the user's selected unit
  // (the backend applies the unit config), so they are displayed as-is.
  const parametersToDisplay = parameters.filter((item) =>
    selectedParameters.length ? selectedParameters.includes(item.parameterName) : true
  );

  const type =
    devices.find((device) => device.serialNumber === deviceData.serial_number)?.deviceType ||
    DeviceType.MIXED;

  // The photo now lives on the server, so it has to be fetched. The service
  // caches per serial, which matters here: hover cards mount and unmount on
  // every pass over the map.
  const [deviceImage, setDeviceImage] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    resolveDeviceImage(deviceData.serial_number, controller.signal).then((url) => {
      if (!cancelled) setDeviceImage(url);
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [deviceData.serial_number]);

  return (
    <div
      style={{ left: position.x, top: position.y }}
      className="rounded-lg bg-gray-600 dark:bg-background-600 shadow-md p-3 fixed z-[100] min-w-[11.25rem] ml-2"
    >
      {deviceImage && (
        <img
          src={deviceImage}
          alt={deviceData.deviceName}
          className="w-full h-24 object-cover rounded-md mb-2"
        />
      )}
      <div className="text-gray-25 text-xs font-medium pb-2 border-b dark:border-b-gray-800 border-b-gray-500 flex items-center">
        #{deviceData.deviceName}
        <span className="material-icons-outlined text-base leading-4 pl-2 text-gray-300">info</span>
      </div>
      <div>
        {parametersToDisplay.map((item) => {
          return (
            <div
              key={item.parameterName}
              className={`flex justify-between mt-2 text-xs font-medium ${
                stale ? STALE_TEXT_CLASS : 'text-gray-200'
              }`}
            >
              <div className="flex items-center whitespace-nowrap mr-6 font-normal">
                {stale ? (
                  <div className={`h-2 w-2 shrink-0 rounded-full mr-1 ${STALE_BG_CLASS}`} />
                ) : (
                  <CustomParameterBg
                    type={type}
                    parameterKey={item.parameterName}
                    parameterValue={particleData[item.parameterName]}
                    className="h-2 w-2 shrink-0 rounded-full mr-1"
                  />
                )}
                {item.shortName}
              </div>
              {getDisplayableValue(item.parameterName, particleData[item.parameterName])}
            </div>
          );
        })}
      </div>
      {stale && (
        <p className={`pt-2 text-[11px] ${STALE_TEXT_CLASS}`}>
          No data in this period · {lastReportedLabel(asOf)}
        </p>
      )}
      {alert.length > 0 && (
        <div className="border-t dark:border-t-gray-800 border-t-gray-500 pt-2 mt-2">
          <p className="text-error-500 text-sm font-medium">{alert.length} Alert</p>
          <p className="text-gray-200 text-xs font-normal pt-0.5">{alert[0].name}</p>
          <p className="text-gray-400 text-xs font-medium pt-1">
            {moment(alert[0].timestamp).fromNow()}
          </p>
        </div>
      )}
    </div>
  );
}
